/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  bool, func, shape, string,
  oneOf,
} from 'prop-types';
import {
  forgotPasswordLoginEnterEmailEvent,
  forgotPasswordLoginOpenInboxEvent,
  loginLightboxLoadTrack,
  loginPopupLoginHardLockEvent,
  loginPopupLoginSocialLoginEvent,
  loginVerificationEvent,
  signupPopupLoginSignupBeginEvent,
  signupPopupLoginSignupCompleteEvent,
  signupPopupLoginSignupConfirmPasswordEvent,
  signupPopupLoginSignupEnterEmailEvent,
  signupPopupLoginSignupEnterPasswordEvent,
  signupPopupLoginSignupPhoneRegistrationEvent,
  signupPopupLoginSignupEmailOTPEvent,
  signupPopupLoginSignupOTPVerificationEvent,
  signupPopupLoginSignupOTPResendEvent,
  signupPopupLoginSignupOTPEmailBackClickEvent,
  signupPopupLoginSignupOTPEmailPageCloseEvent,
  signupPopupLoginSignupStep1SubmissionEvent,
  signupPopupLoginSignupFacebookEmailregisterEvent,
  loginPopupLoginEmailLoginEvent,
  goToSkipLoginSignupEventTrack,
  returnToPreviousLocationFromSkipEventTrack,
  confirmSkipLoginSignupEventTrack,
  withTracking,
} from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import {
  Signup as SignupPage, RegisterReward, EmailSignup as EmailSignupPage, Login as LoginPage,
  OTP as OTPPage, SignupPhone as SignupPhonePage, ForgotPassword as ForgotPasswordPage,
  EmailVerification as EmailVerificationPage, FacebookEmail as FacebookEmailPage,
  LoginButtonsOrder, SkipLoginSignUp as SkipLoginSignUpPage,
} from '@hyphengroup/cag-components';
import withAuth from 'containers/withAuth';
import {
  withCreateAccount, withLogin, withMFARegistration, withEmailRegistration,
  withMFAVerification, withMFAActivation, withMFAResend, withRequestPasswordReset,
  withRedeemSubmission, withMFAEmailRegistration,
} from 'containers/withQueries';
import { compose } from 'react-apollo';
import {
  getLoginRedirectUrl,
  getIsRedirectedFromPopup,
  getRuntimeEnvVariables,
  showStaticTextIfSpecified,
} from 'helpers/Utils';
import { fetchStatus } from 'hooks/useLoginStatusCheck';

function Signup({
  track,
  isOpen,
  overlay,
  fullscreen,
  onClose,
  createAccount,
  login,
  mfaResend,
  mfaVerification,
  mfaActivation,
  mfaRegistration,
  mfaEmailRegistration,
  requestPasswordReset,
  activateEmailMfaFlow,
  isEmailMfaFlowActivated,
  getOtpAddressValue,
  updateOtpStaticText,
  processing,
  otpResendProcessing,
  rejected,
  validationError,
  staticText,
  images,
  topBannerText,
  registerCtaText,
  registerAdditionalTandC,
  registerUpperDescription,
  setTopBannerText,
  onTopBannerDismissClick,
  onShowBannerFromIframe,
  goTo,
  onBackClick,
  locations,
  prevLocation,
  location,
  emailValue,
  phoneNumber,
  onGoogleClick,
  onFacebookClick,
  previousAttemptIncorrect,
  cancelSocialMediaAuth,
  emailRegistration,
  onSocialPopupCallback,
  onSocialPopupCallbackSuccess,
  checkboxState,
  startLocation,
  useRewardsModal,
  hideMarketingCheckbox,
  signupBtnsOrder,
  isResendOTP,
  showSkip,
  onConfirmSkip,
  loanRecommend,
}) {
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [signupMethod, setSignupMethod] = useState('');
  const [isSubscribed, setSubscribed] = useState(false);
  const [marketingCheckbox, setMarketingCheckbox] = useState(checkboxState === 'PRETICKED');
  const [emailForResend, setEmailForResend] = useState({ email: "" });

  // eslint-disable-next-line no-param-reassign
  staticText['2FA_PHONE_VERIFICATION_PHONE_FIELD_PREFIX'] = getRuntimeEnvVariables('RUNTIME_2FA_PHONE_VERIFICATION_PHONE_FIELD_PREFIX');
  const countryCode = staticText['2FA_PHONE_VERIFICATION_PHONE_FIELD_PREFIX'];
  const checkboxProps = { checkbox: marketingCheckbox };
  const shouldDisplayOTPVerification = getRuntimeEnvVariables('RUNTIME_ENABLE_OTP_RESEND_BUTTON') === 'true'

  if (!isSubscribed && !hideMarketingCheckbox) {
    switch (checkboxState) {
      case 'UNTICKED':
        checkboxProps.checkboxDefaults = { ticked: false };
        break;
      case 'PRETICKED':
        checkboxProps.checkboxDefaults = { ticked: true };
        break;
      case 'DISABLED':
      default:
        // Hide checkbox by not adding checkboxDefaults prop
    }
  }

  const onEmailSignupSubmit = ({ email, password }) => {
    createAccount(
      { email, password },
      (data) => {
        signupPopupLoginSignupStep1SubmissionEvent(track, data);
        if (data?.subscribed) {
          setSubscribed(data?.subscribed);
        }
      },
    );
  };

  const onEmailRegistrationSubmit = (formData) => {
    emailRegistration({ email: formData?.email });
  };

  const onPhoneSignupSubmit = (formData) => {
    const params = { phoneNumberInput: { countryCode, phoneNumber: formData?.phoneNumber } };
    // Only subscribe user if marketing checkbox is ticked
    if (formData?.checkbox || startLocation === 'REWARDS_REGISTER') {
      params.newsletterSubscription = true;
    }
    mfaRegistration(
      params,
      (data) => signupPopupLoginSignupPhoneRegistrationEvent(track, data, formData)
    );
  };

  const onOTPActivationSubmit = (formData) => {
    mfaActivation(
      { otpCode: formData?.otpCode },
      (data) => {
        signupPopupLoginSignupOTPVerificationEvent(track, data, isEmailMfaFlowActivated());
        return signupPopupLoginSignupCompleteEvent(track, signupMethod, data);
      },
    );
  };

  const onMfaOtpResend = () => {
    mfaResend();
    signupPopupLoginSignupOTPResendEvent(track);
  };

  const onOTPVerificationByEmailClick = (formData) => {
    activateEmailMfaFlow(true);
    const params = { email: formData?.email };
    mfaEmailRegistration(
      params,
      () => signupPopupLoginSignupEmailOTPEvent(track)
    );
  };

  const onOTPVerificationSubmit = (formData) => {
    mfaVerification(
      { otpCode: formData?.otpCode },
      (data) => loginVerificationEvent(track, data, isEmailMfaFlowActivated())
    );
  };

  const onMfaOtpPageBackClick = () => {
    if (isEmailMfaFlowActivated()) signupPopupLoginSignupOTPEmailBackClickEvent(track);
    activateEmailMfaFlow(false);
    onBackClick();
  };

  const onMfaOtpActivationPageClose = () => {
    if (isEmailMfaFlowActivated()) signupPopupLoginSignupOTPEmailPageCloseEvent(track);
    onClose();
  };

  const onEmailSignupClick = () => {
    cancelSocialMediaAuth();
    signupPopupLoginSignupBeginEvent(track, 'email');
    setSignupMethod('email');
    goTo(locations.EMAIL_SIGNUP);
  };

  const onForgotPasswordClick = () => {
    cancelSocialMediaAuth();
    goTo(locations.RESET_PASSWORD);
  };

  const onEmailLoginClick = () => {
    cancelSocialMediaAuth();
    setTopBannerText('Redirect to login here');
  };

  const onAlreadyAMemberClick = () => {
    cancelSocialMediaAuth();
    goTo(locations.LOGIN);
  };

  const onToggleShowHidePassword = () => {
    setIsPasswordShown(!isPasswordShown);
  };

  const onForgotPasswordSubmit = ({ email }) => {
    requestPasswordReset({ email });
    setEmailForResend({ email });
  };

  const onResendPasswordClick = () => {
    requestPasswordReset(emailForResend);
  };

  const onSignupClick = () => {
    cancelSocialMediaAuth();

    let nextLocation = locations.SIGNUP;
    if (prevLocation === locations.REWARDS_REGISTER) {
      nextLocation = locations.REWARDS_REGISTER;
    }
    goTo(nextLocation);
  };

const onLogin = (details) => {
    cancelSocialMediaAuth();
    login(
      details,
      (data) => {
        if (data?.subscribed) {
          setSubscribed(data?.subscribed);
        }
      }
    );
  };

  const onSkipLoginClick = () => {
    if (loanRecommend) {
        goToSkipLoginSignupEventTrack(track, location, staticText);
    }
    goTo(locations.SKIP_LOGIN_OR_SIGNUP);
  };

  const onSkipSignUpClick = () => {
    if (loanRecommend) {
      goToSkipLoginSignupEventTrack(track, location, staticText);
    }
    goTo(locations.SKIP_LOGIN_OR_SIGNUP);
  };

  const onConfirmSkipLoginSignUpClick = () => {
    if (loanRecommend) {
      confirmSkipLoginSignupEventTrack(track, prevLocation, staticText);
    }
    onConfirmSkip();
  };

  const onReturnToPreviousLocationClick = () => {
    if (loanRecommend) {
      returnToPreviousLocationFromSkipEventTrack(track, prevLocation, staticText);
    }
    goTo(prevLocation);
  };

  const onCheckboxChange = ({ target: { checked } }) => {
    setMarketingCheckbox(checked);
  };

  useEffect(() => {
    if (isOpen) {
      loginLightboxLoadTrack(track, location, loanRecommend, prevLocation);
    }
  }, [location, isOpen]);

  useEffect(() => {
    if (isOpen && previousAttemptIncorrect) {
      loginPopupLoginHardLockEvent(track);
    }
  }, [location, isOpen, previousAttemptIncorrect]);

  useEffect(() => {
    if (getIsRedirectedFromPopup()) {
      onSocialPopupCallback(onSocialPopupCallbackSuccess);
    }
  }, []);

  // Check if social sign-on user is already subscribed
  useEffect(() => {
    if (location === locations.MFA_REGISTRATION) {
      fetchStatus({
        onSuccess: (response) => {
          if (response?.subscribed) {
            setSubscribed(response.subscribed);
          }
        },
        onError: (e) => { throw new Error(e); },
      });
    }
  }, [location]);
  switch (location) {
    case locations.SIGNUP: return (
      useRewardsModal
        ? (
          <RegisterReward
              isOpen={isOpen}
              overlay={overlay}
              staticText={staticText}
              heroImage={images?.SIGNUP_HERO?.image}
              handlers={{
                onEmailSignupClick,
                onGoogleSignupClick: () => {
                  signupPopupLoginSignupBeginEvent(track, 'google');
                  setSignupMethod('google');
                  return onGoogleClick();
                },
                onFacebookSignupClick: () => {
                  signupPopupLoginSignupBeginEvent(track, 'facebook');
                  setSignupMethod('facebook');
                  return onFacebookClick();
                },
                onTopBannerDismissClick,
                onAlreadyAMemberClick,
                onClose,
              }}
              topBannerText={topBannerText}
              registerUpperText={staticText.SIGNUP_PURCHASE_HEADER}
              registerUpperDescription={staticText.SIGNUP_PURCHASE_DESCRIPTION}
            />
        )
        : (
          <SignupPage
            isOpen={isOpen}
            staticText={{
              ...staticText,
              LOGIN_HEADER_SUBTEXT: showStaticTextIfSpecified('LOGIN_HEADER_SUBTEXT', staticText),
            }}
            heroImage={images?.SIGNUP_HERO?.image}
            handlers={{
              onEmailSignupClick,
              onGoogleSignupClick: () => {
                signupPopupLoginSignupBeginEvent(track, 'google');
                setSignupMethod('google');
                return onGoogleClick();
              },
              onFacebookSignupClick: () => {
                signupPopupLoginSignupBeginEvent(track, 'facebook');
                setSignupMethod('facebook');
                return onFacebookClick();
              },
              onTopBannerDismissClick,
              onAlreadyAMemberClick,
              onClose,
              onSkipSignUpClick,
            }}
            overlay={overlay}
            topBannerText={topBannerText}
            order={signupBtnsOrder}
            showSkip={showSkip}
            onConfirmSkip={onConfirmSkip}
          />
        )
    );
    case locations.REWARDS_REGISTER: return (
      <RegisterReward
        staticText={staticText}
        registerUpperText={registerCtaText}
        registerAdditionalInfo={registerAdditionalTandC}
        registerUpperDescription={registerUpperDescription}
        handlers={{
          onEmailSignupClick,
          onGoogleSignupClick: () => {
            signupPopupLoginSignupBeginEvent(track, 'google');
            setSignupMethod('google');
            return onGoogleClick();
          },
          onFacebookSignupClick: () => {
            signupPopupLoginSignupBeginEvent(track, 'facebook');
            setSignupMethod('facebook');
            return onFacebookClick();
          },
          onTopBannerDismissClick,
          onAlreadyAMemberClick,
          onClose,
        }}
        heroImage={images?.SIGNUP_HERO?.image}
        overlay={overlay}
      />
    );

    case locations.EMAIL_SIGNUP: return (
      <EmailSignupPage
        isOpen={isOpen}
        staticText={staticText}
        heroImage={images?.SIGNUP_HERO?.image}
        heroImage2={images?.EMAIL_SIGNUP_HERO?.image}
        handlers={{
          onTopBannerDismissClick,
          onAlreadyAMemberClick,
          onBackClick,
          onSubmit: onEmailSignupSubmit,
          onClose,
          onEmailBlur: (e) => signupPopupLoginSignupEnterEmailEvent(track, e.status),
          onPasswordBlur: (e) => signupPopupLoginSignupEnterPasswordEvent(track, e.status),
          onPasswordConfirmBlur:
            (e) => signupPopupLoginSignupConfirmPasswordEvent(track, e.status),
        }}
        topBannerText={topBannerText}
        loginHeaderSubtext={showStaticTextIfSpecified('LOGIN_HEADER_SUBTEXT', staticText)}
        loading={processing}
        overlay={overlay}
      />
    );

    case locations.EMAIL_REGISTRATION: return (
      <FacebookEmailPage
        isOpen
        heroImage={images?.LOGIN_EMAIL_HERO?.image}
        heroImage2={images?.LOGIN_EMAIL_HERO?.image}
        staticText={staticText}
        loading={processing}
        topBannerText={topBannerText}
        error={rejected}
        handlers={{
          onTopBannerDismissClick,
          onClose,
          onSubmit: onEmailRegistrationSubmit,
          onEmailBlur: (e) => signupPopupLoginSignupFacebookEmailregisterEvent(track, e.status),
        }}
        overlay={overlay}
        fullscreen={fullscreen}
      />
    );

    case locations.MFA_REGISTRATION:
      return (
        <SignupPhonePage
          isOpen={isOpen}
          heroImage={images?.LOGIN_PHONE_HERO?.image}
          heroImage2={images?.POP_UP_PHONE?.image}
          staticText={staticText}
          handlers={{
            onClose,
            onSubmit: onPhoneSignupSubmit,
            onTopBannerDismissClick,
            onCheckboxChange,
          }}
          defaultPhoneNumber={phoneNumber}
          loading={processing}
          topBannerText={topBannerText}
          invalidPhoneValue={validationError}
          error={rejected}
          overlay={overlay}
          fullscreen={fullscreen}
          dataSelectorPrefix="signup-mfa"
          {...checkboxProps}
        />
      );

    case locations.MFA_ACTIVATION:
      return (
        <OTPPage
          isOpen={isOpen || getLoginRedirectUrl()}
          heroImage={images?.LOGIN_PHONE_HERO?.image}
          heroImage2={images?.LOGIN_PHONE_HERO?.image}
          staticText={updateOtpStaticText(staticText, true)}
          handlers={{
            onResend: onMfaOtpResend,
            onSubmit: onOTPActivationSubmit,
            onOTPVerificationByEmailClick,
            onBackClick: onMfaOtpPageBackClick,
            onTopBannerDismissClick,
            onShowBannerFromIframe,
          }}
          loading={processing}
          otpResendLoading={otpResendProcessing}
          error={rejected}
          topBannerText={topBannerText}
          invalidOtpValue={validationError}
          otpReceiverId={getOtpAddressValue()}
          overlay={overlay}
          fullscreen={fullscreen}
          dataSelectorPrefix="signup-otp"
          isResendOTP={isResendOTP}
        />
      );

    case locations.MFA_VERIFICATION:
      return (
        <OTPPage
          isOpen={isOpen || getLoginRedirectUrl()}
          heroImage={images?.LOGIN_PHONE_HERO?.image}
          heroImage2={images?.LOGIN_PHONE_HERO?.image}
          staticText={updateOtpStaticText(staticText, false)}
          handlers={{
            onResend: onMfaOtpResend,
            onSubmit: onOTPVerificationSubmit,
            onOTPVerificationByEmailClick,
            onBackClick: onMfaOtpPageBackClick,
            onTopBannerDismissClick,
            onClose: onMfaOtpActivationPageClose,
            onShowBannerFromIframe,
            onSmsOtpResend: onMfaOtpResend,
          }}
          loading={processing}
          otpResendLoading={otpResendProcessing}
          error={rejected}
          topBannerText={topBannerText}
          invalidOtpValue={validationError}
          otpReceiverId={getOtpAddressValue()}
          overlay={overlay}
          fullscreen={fullscreen}
          dataSelectorPrefix="signup-otp"
          isResendOTP={isResendOTP}
          isMfaVerification={shouldDisplayOTPVerification}
        />
      );

    case locations.RESET_PASSWORD:
      return (
        <ForgotPasswordPage
          isOpen={isOpen}
          heroImage={images?.EMAIL_SIGNUP_HERO?.image}
          staticText={staticText}
          handlers={{
            onSubmit: onForgotPasswordSubmit,
            onRememberPasswordLinkClick: onAlreadyAMemberClick,
            onClose,
            onTopBannerDismissClick,
            onEmailBlur: (e) => forgotPasswordLoginEnterEmailEvent(track, e.status),
          }}
          loading={processing}
          defaultPhoneValue={phoneNumber}
          topBannerText={topBannerText}
          error={rejected}
          overlay={overlay}
          fullscreen={fullscreen}
        />
      );
    case locations.EMAIL_VERIFICATION:
      return (
        <EmailVerificationPage
          isOpen={isOpen}
          heroImage={images?.LOGIN_EMAIL_HERO?.image}
          heroImage2={images?.LOGIN_EMAIL_HERO?.image}
          header={staticText.EMAIL_VERIFICATION_HEADER}
          descriptionSent={staticText.EMAIL_VERIFICATION_DESCRIPTION}
          resendEmailLink={staticText.RESEND_EMAIL_TEXT_LINK}
          resendEmailCountdownText={staticText.RESEND_EMAIL_COUNTDOWN_TEXT}
          resendEmailSent={staticText.RESEND_EMAIL_SENT}
          resendEmailText={staticText.RESEND_EMAIL_TEXT}
          buttonText={staticText.OPEN_MAIL_APP_CTA}
          resendEmailLinkText={staticText.RESEND_EMAIL_TEXT_LINK}
          descriptionCheck={staticText.EMAIL_VERIFICATION_SPAM}
          handlers={{
            onClose,
            onOpenMailAppClick: () => { console.log('EmailVerificationPage - Open mail app'); return forgotPasswordLoginOpenInboxEvent(track); },
            onResendClick: onResendPasswordClick,
          }}
          email={emailValue}
          overlay={overlay}
          fullscreen={fullscreen}
          hideOpenMailAppBtn
        />
      );
    case locations.EMAIL_DEVICE_VERIFICATION:
      return (
        <EmailVerificationPage
          isOpen={isOpen}
          overlay={false}
          hideOpenMailAppBtn
          showCountdown={false}
          heroImage={images?.LOGIN_EMAIL_HERO?.image}
          heroImage2={images?.LOGIN_EMAIL_HERO?.image}
          email={emailValue}
          header={staticText.EMAIL_VERIFICATION_HEADER}
          descriptionSent={staticText.EMAIL_GUEST_VERIFICATION_DESCRIPTION}
          descriptionCheck={staticText.EMAIL_GUEST_VERIFICATION_DESCRIPTION_REDEEM}
          descriptionResendHeader={staticText.EMAIL_RESEND_HEADER}
          descriptionResend={staticText.EMAIL_RESEND_DESCRIPTION}
          handlers={{
            onClose,
          }}
        />
      );
    case locations.LOGIN:
      return (
        <LoginPage
          isOpen={isOpen}
          staticText={{
            ...staticText,
            LOGIN_HEADER_SUBTEXT: showStaticTextIfSpecified('LOGIN_HEADER_SUBTEXT', staticText),
          }}
          heroImage={images?.SIGNUP_HERO?.image}
          handlers={{
            onEmailLoginClick,
            onGoogleLoginClick: () => { loginPopupLoginSocialLoginEvent(track, 'google'); return onGoogleClick(); },
            onFacebookLoginClick: () => { loginPopupLoginSocialLoginEvent(track, 'facebook'); return onFacebookClick(); },
            onTopBannerDismissClick,
            onAlreadyAMemberClick,
            onToggleShowHidePassword,
            onSignupClick,
            onSubmit: (details) => {
              loginPopupLoginEmailLoginEvent(track);
              return onLogin(details);
            },
            onForgotPasswordClick,
            onClose,
            onSkipLoginClick,
          }}
          topBannerText={topBannerText}
          loading={processing}
          previousAttemptIncorrect={previousAttemptIncorrect}
          overlay={overlay}
          showSkip={showSkip}
          onConfirmSkip={onConfirmSkip}
        />
      );
      case locations.SKIP_LOGIN_OR_SIGNUP:
          return (
            <SkipLoginSignUpPage
              isOpen={isOpen}
              staticText={{
                  ...staticText,
                  LOGIN_HEADER_SUBTEXT: showStaticTextIfSpecified('LOGIN_HEADER_SUBTEXT', staticText),
              }}
              heroImage={images?.SKIP_LOGIN_SIGNUP?.image}
              handlers={{
                onClose,
                onTopBannerDismissClick,
                onConfirmSkipLoginSignUpClick,
                onReturnToPreviousLocationClick,
              }}
              topBannerText={topBannerText}
              loading={processing}
              previousAttemptIncorrect={previousAttemptIncorrect}
              overlay={overlay}
              showSkip={showSkip}
              onConfirmSkip={onConfirmSkip}
            />
          );

    default: return 'Unknown state';
  }
}

Signup.propTypes = {
  track: func,
  startLocation: string,
  isOpen: bool.isRequired,
  overlay: bool,
  fullscreen: bool,
  onClose: func,
  onSocialPopupCallbackSuccess: func,
  createAccount: func.isRequired,
  login: func.isRequired,
  mfaResend: func.isRequired,
  mfaVerification: func.isRequired,
  mfaActivation: func.isRequired,
  processing: bool,
  otpResendProcessing: bool,
  rejected: bool,
  staticText: shape({}).isRequired,
  images: shape({
    SIGNUP_HERO: shape({
      image: shape({}).isRequired,
    }).isRequired,
    EMAIL_SIGNUP_HERO: shape({
      image: shape({}).isRequired,
    }).isRequired,
    LOGIN_EMAIL_HERO: shape({
      image: shape({}).isRequired,
    }).isRequired,
    LOGIN_PHONE_HERO: shape({
      image: shape({}).isRequired,
    }).isRequired,
    SKIP_LOGIN_SIGNUP: shape({
      image: shape({}).isRequired,
    }).isRequired,
  }).isRequired,
  topBannerText: string,
  registerCtaText: string,
  registerAdditionalTandC: string,
  registerUpperDescription: string,
  setTopBannerText: func.isRequired,
  onTopBannerDismissClick: func.isRequired,
  goTo: func.isRequired,
  onBackClick: func.isRequired,
  validationError: bool,
  locations: shape({}).isRequired,
  prevLocation: string,
  location: string.isRequired,
  emailValue: string,
  phoneNumber: string,
  onGoogleClick: func.isRequired,
  onFacebookClick: func.isRequired,
  cancelSocialMediaAuth: func.isRequired,
  previousAttemptIncorrect: bool,
  customerEmail: string,
  signupBtnsOrder: oneOf([LoginButtonsOrder.EGF, LoginButtonsOrder.GEF, LoginButtonsOrder.FGE]),
  isFrame: bool,
  showSkip: bool,
  onConfirmSkip: func,
  loanRecommend: bool,
};

Signup.defaultProps = {
  track: null,
  overlay: true,
  fullscreen: true,
  processing: false,
  otpResendProcessing: false,
  rejected: false,
  topBannerText: '',
  registerCtaText: '',
  registerAdditionalTandC: '',
  registerUpperDescription: null,
  validationError: false,
  prevLocation: '',
  emailValue: '',
  phoneNumber: '',
  previousAttemptIncorrect: false,
  isFrame: false,
  showSkip: false,
  loanRecommend: false,
};

export default (
  withApplicationConfig(
    compose(
      withTracking,
      withCreateAccount,
      withLogin,
      withMFARegistration,
      withEmailRegistration,
      withMFAEmailRegistration,
      withMFAVerification,
      withMFAActivation,
      withMFAResend,
      withRequestPasswordReset,
      withRedeemSubmission,
      withAuth,
    )(Signup), ['languages', 'marketingConsent', 'routes']
  ));
